import { memo } from "react";

import type { Config } from "@atlaskit/react-ufo/config";
import { init as initReactUFO } from "@atlaskit/react-ufo/interaction-metrics-init";
import { configure as configureResourceTimings } from "@atlaskit/react-ufo/resource-timing";
import traceUFOPageLoad from "@atlaskit/react-ufo/trace-pageload";
import AnalyticsWebClient from "@atlassiansox/analytics-web-client";

import { getResourceTimingsConfig } from "./get-resource-config";

let isReactUFOInitialized = false;

const config: Config = {
    product: "trello",
    region: "unknown",
    kind: {
        page_load: 1,
        transition: 1,
        hover: 0,
        legacy: 1,
        typing: 0,
        press: 1
    },
    capability: {
        feature_flag_access: 100,
        react_profiler: 1
    },
    segmentPrefix: "ufo-",
    typingMethod: "timeout",
    vc: {
        enabled: true,
        heatmapSize: 200,
        oldDomUpdates: false,
        devToolsEnabled: true,
        selectorConfig: {
            id: false,
            role: true,
            className: true,
            testId: true
        }
    }
};

/**
 * Imperatively initialize UFO for all Trello environments — call this BEFORE React renders.
 * Mirrors the wac-web `initUFO(analyticsClient, path)` bootstrap pattern so that
 * traceUFOPageLoad fires after the subscriber is already set up.
 */
export const initUFOTrello = (
    analyticsClient: AnalyticsWebClient,
    path: string
) => {
    if (isReactUFOInitialized) return;

    try {
        configureResourceTimings(getResourceTimingsConfig());
        initReactUFO(Promise.resolve(analyticsClient), config);
        isReactUFOInitialized = true;

        const routeKey = path.replace(/^\//, "").split("/")[0] || "home";
        const experienceName = `trello-portal-${routeKey}-load`;
        const normalizedPath = path.startsWith("/") ? path : `/${path}`;
        traceUFOPageLoad(experienceName, normalizedPath);

        if (typeof window !== "undefined") {
            (
                window as Window & {
                    __ufoDebug?: {
                        setupAttemptedAt: number;
                        initialized: boolean;
                        hasAnalyticsClient: boolean;
                        initializedAt?: number;
                    };
                    __ufoTrelloDebug?: {
                        trackedAt: number;
                        path: string;
                        experienceName: string;
                    };
                }
            ).__ufoDebug = {
                setupAttemptedAt: Date.now(),
                initialized: true,
                initializedAt: Date.now(),
                hasAnalyticsClient: true
            };
            (
                window as Window & {
                    __ufoTrelloDebug?: {
                        trackedAt: number;
                        path: string;
                        experienceName: string;
                    };
                }
            ).__ufoTrelloDebug = {
                trackedAt: Date.now(),
                path,
                experienceName
            };
        }
    } catch (error) {
        console.error("[UFO Trello] Setup failed:", error);
    }
};

/**
 * React component shim — kept for layout tree compatibility.
 * Actual init now happens imperatively in init-trello.tsx before React renders.
 */
export const InitUFOTrello = memo(() => null);
InitUFOTrello.displayName = "InitUFOTrello";
